import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Alert, Badge, Button, Card, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { ApiError, api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import { can } from '../../lib/permissions';
import { formatDate, formatMoney } from '../people/data';
import { PeopleNav } from '../people/PeopleNav';
import { daysUntil, type LicenseList } from './data';
import { LicenseForm } from './LicenseForm';

/** Aviso de renovación (guía 7.6): las anuales que renuevan en 45 días o menos se resaltan. */
const RENEWAL_SOON_DAYS = 45;

/** Licencias (guía 7.6): puestos totales, asignados y disponibles, y los costos mensuales y anuales. */
export function LicensesPage() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ['licencias', 'lista'], queryFn: () => api<LicenseList>('/api/licencias') });
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-tinta">{t('li.titulo')}</h1>
          <p className="max-w-2xl text-sm text-slate-600">{t('li.descripcion')}</p>
        </div>
        {list.data?.can.create && !creating && <Button onClick={() => setCreating(true)}>{t('li.nueva')}</Button>}
      </div>
      <PeopleNav />

      {creating && (
        <Card title={t('li.nueva')}>
          <LicenseForm
            submitLabel={t('li.crear')}
            onCancel={() => setCreating(false)}
            onSubmit={async (values) => {
              const created = await api<{ id: string }>('/api/licencias', { json: values });
              await qc.invalidateQueries({ queryKey: ['licencias'] });
              void navigate(`/licencias/${created.id}`);
            }}
          />
        </Card>
      )}

      {!list.data ? (
        list.error ? <Alert>{errorMessage(list.error)}</Alert> : <Spinner label={t('comun.cargando')} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <Card>
              <p className="text-xs text-slate-500">{t('li.total_mes')}</p>
              <p className="text-2xl font-semibold tabular-nums text-slate-900">{formatMoney(list.data.totals.monthly)}</p>
            </Card>
            <Card>
              <p className="text-xs text-slate-500">{t('li.total_anio')}</p>
              <p className="text-2xl font-semibold tabular-nums text-slate-900">{formatMoney(list.data.totals.annual)}</p>
            </Card>
          </div>
          <Card className="p-0">
            {list.data.items.length === 0 ? (
              <p className="p-5 text-sm text-slate-500">{t('li.vacio')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-slate-200 text-xs text-slate-500">
                    <tr>
                      <th className="px-4 py-2 font-medium">{t('li.col_licencia')}</th>
                      <th className="px-4 py-2 font-medium">{t('li.col_puestos')}</th>
                      <th className="px-4 py-2 font-medium">{t('li.col_costo_puesto')}</th>
                      <th className="px-4 py-2 font-medium">{t('li.col_mes')}</th>
                      <th className="px-4 py-2 font-medium">{t('li.col_anio')}</th>
                      <th className="px-4 py-2 font-medium">{t('li.col_renovacion')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {list.data.items.map((l) => {
                      const soon = l.active && l.billing === 'anual' && l.renewalDate !== null && daysUntil(l.renewalDate) <= RENEWAL_SOON_DAYS;
                      return (
                        <tr key={l.id} className={`hover:bg-slate-50 ${l.active ? '' : 'text-slate-400'}`}>
                          <td className="px-4 py-2">
                            <Link to={`/licencias/${l.id}`} className="font-medium text-slate-900 hover:underline">
                              {l.name}
                            </Link>
                            {!l.active && (
                              <span className="ml-1.5">
                                <Badge>{t('li.inactiva')}</Badge>
                              </span>
                            )}
                            <span className="block text-xs text-slate-500">
                              {t(l.billing === 'mensual' ? 'li.mensual' : 'li.anual')}
                              {l.shared && ` · ${t('li.compartida')}`}
                              {l.platform && ` · ${l.platform}`}
                              {l.owner && ` · ${t('li.administra', { nombre: l.owner })}`}
                            </span>
                          </td>
                          <td className="px-4 py-2 tabular-nums text-slate-700">
                            {l.seatsPurchased === null ? t('li.puestos_sin_tope', { asignados: l.assigned }) : t('li.puestos_de', { asignados: l.assigned, total: l.seatsPurchased })}
                            {l.costs.available !== null && (
                              <span className={`block text-xs ${l.costs.available === 0 ? 'text-amber-700' : 'text-slate-500'}`}>{t('li.disponibles', { n: l.costs.available })}</span>
                            )}
                          </td>
                          <td className="px-4 py-2 tabular-nums text-slate-700">{formatMoney(l.baseCost)}</td>
                          <td className="px-4 py-2 tabular-nums text-slate-700">{formatMoney(l.costs.monthly)}</td>
                          <td className="px-4 py-2 tabular-nums text-slate-700">{formatMoney(l.costs.annual)}</td>
                          <td className="px-4 py-2 text-slate-700">
                            {l.renewalDate ? (
                              <span className={soon ? 'font-medium text-amber-800' : ''}>
                                {formatDate(l.renewalDate)}
                                {soon && <span className="block text-xs">{t('li.renueva_en', { n: daysUntil(l.renewalDate) })}</span>}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
          {can(me, 'licencias.gestionar') && <ImportAssignmentsCard />}
        </>
      )}
    </div>
  );
}

/** Importación inicial de asignaciones (guía 7.6): correo;licencia;desde. Todo o nada. */
function ImportAssignmentsCard() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lineErrors, setLineErrors] = useState<Array<{ line: number; message: string }>>([]);
  const [result, setResult] = useState<{ created: number; skipped: number } | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    setLineErrors([]);
    try {
      setResult(await api<{ created: number; skipped: number }>('/api/licencias/importar', { json: { csv } }));
      await qc.invalidateQueries({ queryKey: ['licencias'] });
    } catch (err) {
      setError(errorMessage(err));
      setLineErrors(err instanceof ApiError ? ((err.body as { errors?: Array<{ line: number; message: string }> } | undefined)?.errors ?? []) : []);
    } finally {
      setBusy(false);
    }
  }

  if (!open)
    return (
      <Button variant="link" className="text-sm" onClick={() => setOpen(true)}>
        {t('li.importar_abrir')}
      </Button>
    );
  return (
    <Card title={t('li.importar')} className="max-w-3xl">
      <div className="space-y-3">
        <p className="text-sm text-slate-600">{t('li.importar_ayuda')}</p>
        <pre className="overflow-x-auto rounded-md bg-slate-50 p-3 text-xs text-slate-700">{'correo;licencia;desde\nmaria@empresa.com;Microsoft 365 E3;2026-01-01'}</pre>
        <div className="space-y-1">
          <label htmlFor="li-archivo" className="block text-sm font-medium text-slate-700">
            {t('li.importar_archivo')}
          </label>
          <input
            id="li-archivo"
            type="file"
            accept=".csv,text/csv,text/plain"
            className="text-sm"
            onChange={async (e) => {
              setResult(null);
              setCsv((await e.target.files?.[0]?.text()) ?? '');
            }}
          />
        </div>
        {result && <Alert tone="success">{t('li.importar_ok', { creadas: result.created, saltadas: result.skipped })}</Alert>}
        {error && (
          <Alert>
            <p>{error}</p>
            {lineErrors.length > 0 && (
              <ul className="mt-1 list-disc pl-5">
                {lineErrors.map((e) => (
                  <li key={`${e.line}-${e.message}`}>{t('us.importar_linea', { linea: e.line, motivo: e.message })}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}
        <div className="flex gap-2">
          <Button disabled={busy || !csv.trim()} onClick={() => void submit()}>
            {busy ? t('us.importando') : t('us.importar_boton')}
          </Button>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            {t('comun.cancelar')}
          </Button>
        </div>
      </div>
    </Card>
  );
}
